import React, {Fragment} from 'react';
import PropTypes from 'prop-types';
import block from 'bem-cn-lite';
import {fieldPropTypes} from 'redux-form';
import {TextInput} from '@yandex-data-ui/common';
import _ from 'lodash';

import {KeyCodes} from '../../../constants';
import {formatNumber} from 'utils';

import './FormFieldNumberInput.scss';

const b = block('FormFieldNumberInput');
const MINUS_SIGN = '−';
const PLUS_SIGN = '+';
const MAX_VALUE_LENGTH = 15;

export class FormFieldNumberInput extends React.Component {
    static propTypes = {
        ...fieldPropTypes,
        step: PropTypes.number,
        displayFormat: PropTypes.func,
        className: PropTypes.string,
        readOnly: PropTypes.bool,
        range: PropTypes.object,
        defaultValue: PropTypes.number,
        withoutControls: PropTypes.bool,
        maxLength: PropTypes.number,
    };

    static defaultProps = {
        step: 1,
        displayFormat: formatNumber,
        maxLength: MAX_VALUE_LENGTH,
    };

    state = {
        inputText: this.props.displayFormat(this.props.input.value),
        inputFocused: false,
    };

    componentDidUpdate(prevProps) {
        if (this.props.input.value !== prevProps.input.value && !this.state.inputFocused) {
            this.setState({inputText: this.props.displayFormat(this.props.input.value)});
        }
    }

    stepUp() {
        const {input, step, range, onUpdate} = this.props;
        const next = Number(input.value) + step;

        if (range && range.max && next > range.max) {
            return;
        }

        const value = Math.min(next, Number.MAX_SAFE_INTEGER);

        input.onChange(value);

        if (onUpdate) {
            onUpdate(value);
        }
    }

    stepDown() {
        const {input, step, range, onUpdate} = this.props;

        const next = Number(input.value) - step;

        if (range && range.min && next < range.min) {
            return;
        }

        const value = Math.max(next, 0);

        input.onChange(value);

        if (onUpdate) {
            onUpdate(value);
        }
    }

    handleChange = (value) => {
        const {input, readOnly, maxLength, onUpdate} = this.props;

        if (readOnly) {
            return;
        }
        const regexp = /^[0-9]*$/;

        if (!regexp.test(value) || value.length > maxLength) {
            return;
        }

        this.setState({inputText: value});

        const valueWithDefault = this.getValueWithDefault(value);
        input.onChange(valueWithDefault);

        if (onUpdate) {
            onUpdate(valueWithDefault);
        }
    };

    handleKeyDown = (event) => {
        switch (event.keyCode) {
            case KeyCodes.ARROW_TOP:
                event.preventDefault();
                this.stepUp();
                break;
            case KeyCodes.ARROW_BOTTOM:
                event.preventDefault();
                this.stepDown();
                break;
        }
    };

    handleLeftControlClick = () => {
        this.stepDown();
    };

    handleRightControlClick = () => {
        this.stepUp();
    };

    handleInputFocus = () => {
        const {input, readOnly} = this.props;

        if (readOnly) {
            return;
        }

        this.setState({inputText: String(input.value), inputFocused: true});
    };

    handleInputBlur = () => {
        const {input, displayFormat, readOnly} = this.props;

        if (readOnly) {
            return;
        }

        this.setState({inputText: displayFormat(input.value), inputFocused: false});
    };

    getValueWithDefault = (value) => {
        const {defaultValue} = this.props;
        const resValue = Number(value);

        if (!defaultValue) {
            return resValue;
        }

        return resValue || defaultValue;
    };

    render() {
        const {input, className, readOnly, withoutControls, ...restProps} = this.props;
        const {inputText} = this.state;
        const textInputProps = {
            size: 'l',
            name: input.name,
            id: input.name,
            value: inputText,
            onUpdate: this.handleChange,
            onKeyDown: this.handleKeyDown,
            onFocus: this.handleInputFocus,
            onBlur: this.handleInputBlur,
            className: b('input'),
            controlProps: {pattern: '[0-9]*', readOnly},
            ..._.omit(restProps, ['onUpdate']),
        };
        let content;

        if (withoutControls) {
            content = <TextInput {...textInputProps} />;
        } else {
            content = (
                <Fragment>
                    <div
                        className={b('control', {pos: 'left'})}
                        onClick={this.handleLeftControlClick}
                    >
                        <span>{MINUS_SIGN}</span>
                    </div>
                    <TextInput {...textInputProps} />
                    <div
                        className={b('control', {pos: 'right'})}
                        onClick={this.handleRightControlClick}
                    >
                        <span>{PLUS_SIGN}</span>
                    </div>
                </Fragment>
            );
        }

        return <div className={b(null, className)}>{content}</div>;
    }
}
